import { BaseFeature } from '../../core/base-feature.js';

/**
 * LabyrinthFeature - Frontend feature for The Labyrinth game
 *
 * Controls the desktop overlay maze game from the frontend.
 * Receives full game state including entire maze, all monster positions,
 * and solution path (intel advantage over the desktop user).
 */
export class LabyrinthFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);
        this.currentState = null;
        this.stateCallbacks = new Set();
        this.logCallbacks = new Set();
        this._pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'labyrinth',
            name: 'Labyrinth',
            version: 'v1.0',
            tier: 'external',
            capabilities: [
                'start', 'end', 'get_state',
                'spawn_monster', 'despawn_monster', 'set_monster_ai', 'set_monster_speed',
                'activate_ability', 'place_trap', 'mutate_maze', 'carve_path',
                'set_double_or_nothing',
                'set_fog_thickening', 'set_safe_zone', 'relocate_exit',
                'place_false_exit', 'remove_false_exit', 'place_phantom_path', 'remove_phantom_paths',
                'scramble_controls', 'set_heartbeat_audio', 'send_taunt',
                'set_mercy_timer', 'possess_monster', 'warden_move', 'release_monster'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[Labyrinth] Initializing...');
        console.log('[Labyrinth] Initialized');
    }

    async handleMessage(message) {
        const { action, cmd, payload, messageId } = message;
        const command = cmd || action;

        switch (command) {
            case 'state_update':
                this.currentState = payload;
                this.notifyStateListeners(payload);

                // Emit log events for specific state types
                {
                    const time = payload?.timer?.elapsed ?? null;
                    const cfg = payload?.config || {};
                    if (payload?.type === 'death') {
                        const baseFee = cfg.deathFee || 10;
                        const fee = payload.doubleOrNothing ? baseFee * 2 : baseFee;
                        this.notifyLogListeners({ type: 'kill', message: `Player killed (death #${payload.deaths})`, time });
                        this.notifyLogListeners({ type: 'economy', message: `-$${fee} death penalty`, time });
                    } else if (payload?.type === 'round_complete') {
                        const baseReward = cfg.completionReward || 50;
                        const reward = payload.doubleOrNothing ? baseReward * 2 : baseReward;
                        this.notifyLogListeners({ type: 'complete', message: `Round ${payload.round} complete`, time });
                        this.notifyLogListeners({ type: 'economy', message: `+$${reward} completion reward`, time });
                    } else if (payload?.type === 'game_started') {
                        this.notifyLogListeners({ type: 'round', message: 'Labyrinth started', time });
                    } else if (payload?.type === 'game_ended') {
                        const tab = payload.economyTab || 0;
                        const sign = tab >= 0 ? '+' : '-';
                        this.notifyLogListeners({ type: 'round', message: 'Labyrinth ended', time });
                        this.notifyLogListeners({ type: 'economy', message: `Final tab: ${sign}$${Math.abs(tab)}`, time });
                    } else if (payload?.type === 'monster_spawned') {
                        this.notifyLogListeners({ type: 'info', message: `${payload.monsterType} spawned`, time });
                    } else if (payload?.type === 'monster_despawned') {
                        this.notifyLogListeners({ type: 'info', message: `${payload.monsterType} removed`, time });
                    } else if (payload?.type === 'trap_placed') {
                        this.notifyLogListeners({ type: 'trap', message: `${payload.trapType} trap placed`, time });
                    } else if (payload?.type === 'maze_mutated') {
                        this.notifyLogListeners({ type: 'info', message: `Maze ${payload.mutationType}`, time });
                    } else if (payload?.type === 'ability_activated') {
                        this.notifyLogListeners({ type: 'info', message: `${payload.monsterType} ability activated`, time });
                    } else if (payload?.type === 'trap_triggered') {
                        this.notifyLogListeners({ type: 'trap', message: `Trap triggered`, time });
                    } else if (payload?.type === 'fog_thickened') {
                        this.notifyLogListeners({ type: 'info', message: `Vision shrinking...`, time });
                    } else if (payload?.type === 'safe_zone_expired') {
                        this.notifyLogListeners({ type: 'trap', message: `Safe zone expired — TRAP!`, time });
                    } else if (payload?.type === 'exit_relocated') {
                        this.notifyLogListeners({ type: 'info', message: `Exit relocated`, time });
                    } else if (payload?.type === 'false_exit_triggered') {
                        this.notifyLogListeners({ type: 'kill', message: `FALSE EXIT!`, time });
                    } else if (payload?.type === 'controls_scrambled') {
                        this.notifyLogListeners({ type: 'info', message: `Controls scrambled`, time });
                    } else if (payload?.type === 'taunt_sent') {
                        this.notifyLogListeners({ type: 'info', message: `Taunt sent`, time });
                    } else if (payload?.type === 'warden_possessed') {
                        this.notifyLogListeners({ type: 'info', message: `Warden mode: possessing ${payload.wardenMode?.possessedMonsterType}`, time });
                    } else if (payload?.type === 'warden_released') {
                        this.notifyLogListeners({ type: 'info', message: `Warden mode released`, time });
                    } else if (payload?.type === 'mercy_timer_reset') {
                        this.notifyLogListeners({ type: 'info', message: `Mercy timer reset`, time });
                    }
                }
                break;

            case 'start_response':
            case 'end_response':
            case 'get_state_response':
            case 'spawn_monster_response':
            case 'despawn_monster_response':
            case 'set_monster_ai_response':
            case 'set_monster_speed_response':
            case 'activate_ability_response':
            case 'place_trap_response':
            case 'mutate_maze_response':
            case 'carve_path_response':
            case 'set_fog_thickening_response':
            case 'set_safe_zone_response':
            case 'relocate_exit_response':
            case 'place_false_exit_response':
            case 'remove_false_exit_response':
            case 'place_phantom_path_response':
            case 'remove_phantom_paths_response':
            case 'scramble_controls_response':
            case 'set_heartbeat_audio_response':
            case 'send_taunt_response':
            case 'set_mercy_timer_response':
            case 'possess_monster_response':
            case 'warden_move_response':
            case 'release_monster_response':
            case 'set_double_or_nothing_response': {
                const originalId = payload?.originalMessageId;
                if (originalId && this._pendingRequests.has(originalId)) {
                    const { resolve } = this._pendingRequests.get(originalId);
                    this._pendingRequests.delete(originalId);
                    resolve(payload);
                }
                break;
            }

            default:
                console.warn(`[Labyrinth] Unknown command: ${command}`);
        }
    }

    _sendRequest(action, payload = {}) {
        return new Promise((resolve, reject) => {
            const messageId = this.sendMessage(action, payload);
            const timeout = setTimeout(() => {
                this._pendingRequests.delete(messageId);
                reject(new Error(`Request '${action}' timed out`));
            }, 10000);

            this._pendingRequests.set(messageId, {
                resolve: (result) => {
                    clearTimeout(timeout);
                    resolve(result);
                },
                reject
            });
        });
    }

    // --- Public API ---

    startGame(config) {
        return this._sendRequest('start', config);
    }

    endGame() {
        return this._sendRequest('end');
    }

    getState() {
        return this._sendRequest('get_state');
    }

    spawnMonster(type) {
        return this._sendRequest('spawn_monster', { type });
    }

    despawnMonster(type) {
        return this._sendRequest('despawn_monster', { type });
    }

    setMonsterAi(type, ai) {
        return this._sendRequest('set_monster_ai', { type, ai });
    }

    setMonsterSpeed(type, speed) {
        return this._sendRequest('set_monster_speed', { type, speed });
    }

    activateAbility(type) {
        return this._sendRequest('activate_ability', { type });
    }

    placeTrap(x, y, type) {
        return this._sendRequest('place_trap', { x, y, type });
    }

    mutateMaze(type) {
        return this._sendRequest('mutate_maze', { type });
    }

    carvePath(walls) {
        return this._sendRequest('carve_path', { walls });
    }

    setDoubleOrNothing(enabled) {
        return this._sendRequest('set_double_or_nothing', { enabled });
    }

    setFogThickening(enabled, rate) {
        return this._sendRequest('set_fog_thickening', { enabled, rate });
    }

    setSafeZone(x, y, duration, trapType) {
        return this._sendRequest('set_safe_zone', { x, y, duration, trapType });
    }

    relocateExit(x, y) {
        return this._sendRequest('relocate_exit', { x, y });
    }

    placeFalseExit(x, y, consequence) {
        return this._sendRequest('place_false_exit', { x, y, consequence });
    }

    removeFalseExit(id) {
        return this._sendRequest('remove_false_exit', { id });
    }

    placePhantomPaths(cells) {
        return this._sendRequest('place_phantom_path', { cells });
    }

    removePhantomPaths() {
        return this._sendRequest('remove_phantom_paths');
    }

    scrambleControls(duration, mode) {
        return this._sendRequest('scramble_controls', { duration, mode });
    }

    setHeartbeatAudio(enabled) {
        return this._sendRequest('set_heartbeat_audio', { enabled });
    }

    sendTaunt(message, duration) {
        return this._sendRequest('send_taunt', { message, duration });
    }

    setMercyTimer(enabled, displaySeconds, behavior) {
        return this._sendRequest('set_mercy_timer', { enabled, displaySeconds, behavior });
    }

    possessMonster(type) {
        return this._sendRequest('possess_monster', { type });
    }

    wardenMove(x, y) {
        return this._sendRequest('warden_move', { x, y });
    }

    releaseMonster() {
        return this._sendRequest('release_monster');
    }

    // --- Event subscriptions for React ---

    onStateUpdate(callback) {
        this.stateCallbacks.add(callback);
        return () => this.stateCallbacks.delete(callback);
    }

    onLogEvent(callback) {
        this.logCallbacks.add(callback);
        return () => this.logCallbacks.delete(callback);
    }

    notifyStateListeners(data) {
        for (const callback of this.stateCallbacks) {
            try {
                callback(data);
            } catch (error) {
                console.error('[Labyrinth] Error in state callback:', error);
            }
        }
    }

    notifyLogListeners(entry) {
        for (const callback of this.logCallbacks) {
            try {
                callback(entry);
            } catch (error) {
                console.error('[Labyrinth] Error in log callback:', error);
            }
        }
    }

    getCurrentState() {
        return this.currentState;
    }

    async cleanup() {
        console.log('[Labyrinth] Cleaning up...');
        this.currentState = null;
        this.stateCallbacks.clear();
        this.logCallbacks.clear();
        for (const [, { reject }] of this._pendingRequests) {
            reject(new Error('Feature cleanup'));
        }
        this._pendingRequests.clear();
    }
}
